/**
 * Native modal overlay wrapper — replaces HeroUI v3 Modal which
 * doesn't render a proper backdrop/centered overlay.
 *
 * Usage: Replace <Modal isOpen={x}><ModalDialog>...</ModalDialog></Modal>
 * with <ModalOverlay isOpen={x} onClose={fn}>...</ModalOverlay>
 */
import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Button, Spinner, cn } from "@heroui/react";

// Track modal depth for stacking nested modals
let modalCount = 0;

// Stack of open-modal close handlers. The topmost entry is the modal that
// should respond to Escape, regardless of where focus currently lives.
const escapeHandlerStack: Array<() => void> = [];

interface ModalOverlayProps {
  isOpen: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "full";
  isDismissable?: boolean;
  /** Accessible name for the dialog (WCAG SC 4.1.2). Falls back to the localised default. */
  ariaLabel?: string;
  /** Id of the element labelling the dialog (e.g. ModalHeader). Takes precedence over ariaLabel. */
  ariaLabelledBy?: string;
  /**
   * Scroll behaviour for content that exceeds the modal's bounds.
   * - "outside" (default): the dialog itself scrolls (existing behaviour).
   * - "inside": the dialog is a bounded flex column; the consumer is
   *   responsible for making one child (typically ModalBody) scroll.
   *   Use this for modals that host their own scrollable content
   *   (iframes, previewers, viewers) so only one scrollbar is visible.
   */
  scrollBehavior?: "inside" | "outside";
}

const sizeClasses: Record<string, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  full: "max-w-[calc(100vw-2rem)]",
};

export const ModalOverlay: React.FC<ModalOverlayProps> = ({
  isOpen,
  onClose,
  onOpenChange,
  children,
  className,
  size = "lg",
  isDismissable = true,
  ariaLabel,
  ariaLabelledBy,
  scrollBehavior = "outside",
}) => {
  const { t } = useTranslation();
  const zIndexRef = React.useRef(0);
  const dialogRef = React.useRef<HTMLDivElement>(null);

  // Latest handleClose via ref so the document-level Escape listener always
  // sees current props without needing to re-attach on every render.
  const handleCloseRef = React.useRef<() => void>(() => {});

  handleCloseRef.current = () => {
    onClose?.();
    onOpenChange?.(false);
  };

  React.useEffect(() => {
    if (isOpen) {
      modalCount++;
      zIndexRef.current = 9999 + modalCount;
    }

    return () => {
      if (isOpen) {
        modalCount = Math.max(0, modalCount - 1);
      }
    };
  }, [isOpen]);

  // Focus the dialog on open so Escape works without a prior click.
  // Skip if a child (e.g. an autoFocus input) already owns focus — otherwise
  // we'd steal it back on every render and break typing.
  React.useEffect(() => {
    if (!isOpen) return;
    const el = dialogRef.current;

    if (el && !el.contains(document.activeElement)) {
      el.focus();
    }
  }, [isOpen]);

  // Document-level Escape handler. Using React's onKeyDown misses Escape when
  // focus leaves the dialog (e.g. native <select> dropdowns, toggle switches).
  // A stack ensures only the topmost modal closes when nested.
  React.useEffect(() => {
    if (!isOpen) return;

    const close = () => handleCloseRef.current();

    escapeHandlerStack.push(close);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const top = escapeHandlerStack[escapeHandlerStack.length - 1];

      if (top === close) {
        e.stopPropagation();
        close();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const idx = escapeHandlerStack.indexOf(close);

      if (idx !== -1) escapeHandlerStack.splice(idx, 1);
    };
  }, [isOpen]);

  const zIndex = zIndexRef.current || 10000;

  const handleClose = () => handleCloseRef.current();

  if (!isOpen) return null;

  const modal = (
    <div
      ref={dialogRef}
      className="fixed inset-0 flex items-center justify-center"
      role="presentation"
      style={{ zIndex }}
      tabIndex={-1}
    >
      {/* Backdrop */}
      {isDismissable ? (
        <button
          aria-label={t("Close modal")}
          className="absolute inset-0 bg-foreground/40 cursor-default"
          type="button"
          onClick={handleClose}
        />
      ) : (
        <div aria-hidden="true" className="absolute inset-0 bg-foreground/40" />
      )}
      {/* Dialog */}
      <div
        aria-label={ariaLabelledBy ? undefined : (ariaLabel ?? t("Dialog"))}
        aria-labelledby={ariaLabelledBy}
        aria-modal="true"
        className={cn(
          "relative z-10 w-full bg-content1 text-content1-foreground rounded-xl shadow-2xl",
          scrollBehavior === "inside"
            ? "h-[85vh] flex flex-col overflow-hidden"
            : "max-h-[85vh] overflow-y-auto",
          sizeClasses[size] || sizeClasses.lg,
          className,
        )}
        role="dialog"
      >
        {/* Close button */}
        <button
          aria-label={t("Close")}
          className={cn(
            "p-1 rounded-full hover:bg-default-100 text-default-500 hover:text-foreground transition-colors z-20",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-default-400",
            scrollBehavior === "inside"
              ? "absolute top-3 right-3"
              : "sticky top-3 float-right mr-3 mt-3",
          )}
          type="button"
          onClick={handleClose}
        >
          <svg
            fill="none"
            height="20"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            width="20"
          >
            <line x1="18" x2="6" y1="6" y2="18" />
            <line x1="6" x2="18" y1="6" y2="18" />
          </svg>
        </button>
        {children}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export const ModalHeader: React.FC<{
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  "data-id"?: string;
}> = ({ children, className, id, ...rest }) => (
  <div
    className={cn("px-6 pt-6 pb-2 text-lg font-semibold", className)}
    id={id ?? rest["data-id"]}
    {...rest}
  >
    {children}
  </div>
);

export const ModalBody: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => <div className={cn("px-6 py-4 flex-1 min-h-0 overflow-y-auto", className)}>{children}</div>;

export const ModalFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={cn("px-6 pb-6 pt-2 flex justify-end gap-2 shrink-0", className)}>{children}</div>
);

/**
 * Props shared by the two footer action buttons. Deliberately a narrow subset
 * of HeroUI's `Button` surface: the point of these components is that a caller
 * passes intent (a label, a handler, a tone) and never styling, so `variant`
 * is not forwardable and `className` is for layout only.
 */
interface ModalActionProps {
  children: React.ReactNode;
  className?: string;
  "aria-label"?: string;
  "data-id"?: string;
  isDisabled?: boolean;
  isPending?: boolean;
  type?: "button" | "submit";
  onPress?: () => void;
  /**
   * Only for a footer that is a control strip rather than an action row, such
   * as the zoom toolbar in the preview modals. An ordinary Cancel-and-confirm
   * pair leaves this alone so every dialogue in the app agrees on button height.
   */
  size?: "sm" | "md" | "lg";
  /**
   * Passthrough for a terminal action that navigates or downloads rather than
   * submitting, which HeroUI renders as an anchor via `as`.
   */
  as?: React.ElementType;
  href?: string;
  rel?: string;
  target?: string;
  download?: string;
}

/**
 * Renders a footer button's label with a leading spinner while the action is in
 * flight.
 *
 * ⚠️ `isPending` on its own is NOT visible feedback. HeroUI maps
 * `[data-pending="true"]` to the `status-pending` utility, which is
 * `pointer-events: none` and nothing else — no spinner, no dimming, no label
 * change (`@heroui/styles/dist/utilities/index.css`). A confirm button given
 * only `isPending` therefore stops responding to clicks with no indication
 * anything is happening, which reads as a frozen dialogue on a slow request.
 *
 * ⚠️ That is a statement about STYLING only. Do not read it as "the press is
 * still live". `isPending` reaches React Aria Components' `Button`, which runs
 * `useDisableInteractions` and sets every `on*` handler except the hover and
 * focus ones to `undefined`, so `onClick`, `onKeyDown` and `onKeyUp` are all
 * gone. It also sets `aria-disabled="true"`, keeps `tabindex="0"`, rewrites a
 * `type="submit"` button to `type="button"` so Enter in a text field cannot
 * implicitly submit, and announces the pending state assertively on entering
 * and again on leaving it. A pending button already refuses both the mouse and
 * the keyboard; it is not left activatable.
 *
 * ⚠️ Passing `isDisabled` alongside `isPending` is therefore not free. It adds
 * the native `disabled` attribute, which takes the button out of the tab order
 * and blurs it, and React Aria's announcement is gated on the button holding
 * focus, so both the "started" and the "finished" announcements are lost. What
 * it buys is HeroUI's `opacity: 0.5` dimming. Several call sites pair the two
 * deliberately for that dimming; if this is revisited, revisit it here rather
 * than one modal at a time.
 *
 * It lives here rather than at each call site because these two components are
 * the single chokepoint for every modal action in the app, so one treatment
 * covers all of them. `PDFPreviewModal` and `WordPreviewModal` had already
 * hand-rolled their own, differently.
 *
 * Exported because the same contract applies to a pending action OUTSIDE a
 * modal footer, where there is no shared button to route through:
 * `BuilderTopBar`'s Save draft and Publish are the current such callers. Reach
 * for this rather than hand-rolling a fourth spinner, and pair it with
 * `aria-busy={isPending || undefined}` as the two components above do.
 *
 * `color="current"` is what makes one spinner correct on both buttons: it
 * inherits the button's text colour, which is dark on the outline Cancel and
 * the paired `--brand-on-primary` on a filled action. The caller's own label is
 * kept rather than replaced, so a modal that already swaps in "Saving…" still
 * reads correctly.
 */
export const PendingContent: React.FC<{ isPending?: boolean; children: React.ReactNode }> = ({
  isPending,
  children,
}) =>
  isPending ? (
    <>
      <Spinner aria-hidden="true" color="current" size="sm" />
      {children}
    </>
  ) : (
    <>{children}</>
  );

/**
 * The dismissive action in a modal footer: a white pill with a grey border.
 *
 * Every dialogue in the app routes its Cancel, Close or Discard-and-stay action
 * through this, so there is one answer to what a dismissive action looks like.
 * Before it existed, thirty five footers spelled it four different ways
 * (`ghost` in 13, `secondary` in 7, `outline` in 5, and twice by accident), and
 * none of the four mistakes below is visible to TypeScript, to ESLint, or to
 * someone reading the diff:
 *
 * 1. A `<Button>` with no `variant` is NOT neutral. HeroUI's `buttonVariants`
 *    declares `defaultVariants: { variant: "primary" }`, so an apparently
 *    unstyled Cancel renders as the loudest control in the dialogue.
 * 2. `color` is a HeroUI v2 prop that v3's `Button` does not have. It used to
 *    type-check because `src/types/heroui-v3-compat.d.ts` re-added
 *    `color?: string` to `ButtonRootProps`, then passed to the DOM inert while
 *    the button fell back to `primary`. This is why a workflow delete used to
 *    offer a blue Delete. That shim declaration has since been removed, so a
 *    `color` on a Button is now a `TS2322` at the call site rather than
 *    something only a grep can see. Do not re-add it.
 * 3. `.btn-brand-primary` is a fill with no notion of rank, so putting it on
 *    both buttons made the dismissive and the terminal action identical.
 * 4. `variant="outline"` only has a border because of a `:not(.button--outline)`
 *    exclusion in `heroui-v2-compat.css`. That file carries an unlayered
 *    `border: none` on `button[data-rac]`, and unlayered beats every layer, so
 *    both HeroUI's own border and a Tailwind `border-*` utility are cancelled.
 *    Do not "simplify" that exclusion away.
 *
 * `outline` is transparent over the `bg-content1` dialog surface, which is what
 * makes it read as white rather than grey, and it needs no `dark:` variant
 * because HeroUI's dark theme block redefines both `--border` and
 * `--default-foreground`.
 */
export const ModalCancelButton: React.FC<ModalActionProps> = ({ children, isPending, ...rest }) => (
  <Button aria-busy={isPending || undefined} isPending={isPending} variant="outline" {...rest}>
    <PendingContent isPending={isPending}>{children}</PendingContent>
  </Button>
);

/** Emphasis levels available to the non-dismissive actions in a footer. */
export type ModalActionTone = "brand" | "neutral" | "danger" | "danger-soft";

const TONE_VARIANTS: Record<ModalActionTone, "primary" | "secondary" | "danger" | "danger-soft"> = {
  brand: "primary",
  neutral: "secondary",
  danger: "danger",
  "danger-soft": "danger-soft",
};

/**
 * Every non-dismissive action in a modal footer: Save, Create, Delete, Export
 * and friends.
 *
 * `brand` is the default because it is the common case, and it pairs
 * `.btn-brand-primary` with `variant="primary"` so the pill follows the active
 * tenant. That class reads `--brand-primary` and its paired
 * `--brand-on-primary`, both published by react-ui's `applyBrand`, whereas
 * HeroUI's `primary` variant alone paints `var(--accent)` — a fixed blue that no
 * tenant token feeds, so the dialogue's main action would be the one control
 * ignoring the brand. The same reasoning is recorded at `automations/index.tsx`.
 *
 * ⚠️ Do NOT reach for `useBrandAccent` here. That hook corrects the tenant's
 * `accent` role for use as opaque TEXT on `bg-content1`. A filled pill uses the
 * `primary` role with its own paired foreground and is contrast-correct by
 * construction. The two roles differ sharply — LSEG is `#001EFF` for `primary`
 * against `#010469` for `accent`.
 *
 * `danger` is kept for destructive confirmations rather than folded into
 * `brand`. Uniformity is not worth making a delete look identical to a save.
 *
 * No radius is set. HeroUI's base `.button` is `rounded-3xl` on an `h-10` box
 * and CSS clamps a radius above half the height, so it is already a true pill.
 */
export const ModalActionButton: React.FC<ModalActionProps & { tone?: ModalActionTone }> = ({
  children,
  className,
  tone = "brand",
  isPending,
  ...rest
}) => (
  <Button
    aria-busy={isPending || undefined}
    className={tone === "brand" ? cn("btn-brand-primary", className) : className}
    isPending={isPending}
    variant={TONE_VARIANTS[tone]}
    {...rest}
  >
    <PendingContent isPending={isPending}>{children}</PendingContent>
  </Button>
);
